import { Phone, Mail, MapPin, Clock } from "lucide-react";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import { BUSINESS } from "../lib/business";

export default function Contact() {
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-bold mb-4">
                            Get in touch
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
                            Ready when you are.
                        </h2>
                        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                            Give us a call or drop us an email — we&apos;ll
                            get back to you the same working day with a
                            straightforward quote.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                data-testid="contact-call-cta"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md transition-all hover:-translate-y-0.5"
                            >
                                <Phone className="w-5 h-5" />
                                Call now
                            </a>
                            <a
                                href={BUSINESS.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-cta"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold shadow-md transition-all hover:-translate-y-0.5"
                            >
                                <WhatsAppIcon className="w-5 h-5" />
                                WhatsApp
                            </a>
                            <a
                                href={`mailto:${BUSINESS.email}`}
                                data-testid="contact-email-cta"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold transition-all"
                            >
                                <Mail className="w-5 h-5" />
                                Send email
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                data-testid="contact-card-phone"
                                className="group block p-7 rounded-xl bg-slate-50 border border-slate-200 hover:border-amber-400 hover:bg-white hover:shadow-md transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-amber-600 group-hover:bg-amber-700 flex items-center justify-center mb-4 transition-colors">
                                    <Phone className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                                    Phone
                                </div>
                                <div className="mt-1 font-display font-bold text-xl text-slate-900 break-all">
                                    {BUSINESS.phone}
                                </div>
                            </a>

                            <a
                                href={BUSINESS.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-card-whatsapp"
                                className="group block p-7 rounded-xl bg-slate-50 border border-slate-200 hover:border-[#25D366] hover:bg-white hover:shadow-md transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-[#25D366] group-hover:bg-[#1ebe57] flex items-center justify-center mb-4 transition-colors">
                                    <WhatsAppIcon className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                                    WhatsApp
                                </div>
                                <div className="mt-1 font-display font-bold text-xl text-slate-900">
                                    Chat with us
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                    Fastest reply — send a message anytime
                                </div>
                            </a>

                            <a
                                href={`mailto:${BUSINESS.email}`}
                                data-testid="contact-card-email"
                                className="group block p-7 rounded-xl bg-slate-50 border border-slate-200 hover:border-amber-400 hover:bg-white hover:shadow-md transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-slate-900 group-hover:bg-slate-800 flex items-center justify-center mb-4 transition-colors">
                                    <Mail className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                                    Email
                                </div>
                                <div className="mt-1 font-display font-bold text-lg text-slate-900 break-all">
                                    {BUSINESS.email}
                                </div>
                            </a>

                            <div
                                data-testid="contact-card-area"
                                className="p-7 rounded-xl bg-slate-50 border border-slate-200"
                            >
                                <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center mb-4">
                                    <MapPin className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                                    Service area
                                </div>
                                <div className="mt-1 font-display font-bold text-lg text-slate-900">
                                    {BUSINESS.area}
                                </div>
                            </div>

                            <div
                                data-testid="contact-card-hours"
                                className="p-7 rounded-xl bg-slate-50 border border-slate-200 sm:col-span-2"
                            >
                                <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center mb-4">
                                    <Clock className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                                    Hours
                                </div>
                                <ul className="mt-2 space-y-1">
                                    {BUSINESS.hours.map((h) => (
                                        <li
                                            key={h.day}
                                            className="flex justify-between gap-3 text-sm"
                                        >
                                            <span className="text-slate-600">
                                                {h.day}
                                            </span>
                                            <span className="font-semibold text-slate-900">
                                                {h.time}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
